import axios from "axios";
import { defineStore } from "pinia";
import { useConversation } from "./Conversation";
import { useWorkspace } from "./Workspaces";
import { useWorkspaceTree } from "./WorkspaceTree";
import { useTeam } from "../Components/Custom/Team/Store/Team";
import Utilities from "../Helpers/utilities";
import { useUserStore } from "./User";
import HttpStatusCodes from "Admin/js/Constants/http.js";
import { useAppState } from "./AppState";
import { useChat } from "../Components/Chat/Store/Chat";
import { isRequestCancelled } from "../Helpers/requestCancellation";
import { getPreferredAssignerProfileId } from "../Composables/useAssignOnBehalfOf";
import { openMembershipTabForConversation, isMembershipDataPrefetchedForConversation, isMembershipTabActiveForConversation } from "../Components/Custom/Team/Composables/useWorkPanelMembershipConversation";
import {
    reconcileQuickConversationAssignmentWithTeamStore,
    syncQuickConversationAssignmentFromTeamStore,
} from "../Components/Custom/Team/Composables/workPanelMembershipAssignmentSync";
import {
    resolveQuickConversationCacheKey,
    resolveQuickConversationSeededCacheKey,
    resolveQuickConversationSelectedUsersFromParticipation,
    syncQuickConversationSelectedUserIds,
} from "../Components/Custom/Team/Composables/workPanelMembershipQuickConversationSelection";
import {
    ensureWorkPanelMemberRoleProfile,
    normalizeMembershipProfileId,
} from "../Components/Custom/Team/Composables/workPanelMembershipRoleProfiles";
import { canEditWorkPanelMembershipFromProfile } from "../Components/Custom/Team/Composables/workPanelMembershipLineageRoles";

export const useInvitation = defineStore("InvitationStore", {
    state: () => ({
        show_invite_model: false,
        user_invitations: [],
        user_profile_ids: [],
        selected_user_profile_ids: [],
        // selected_group_ids: [],
        selected_group_user_profile_ids: [],
        is_retrieving_invited_user_in_progress: false,
        user_profiles_with_roles: [],
        has_conversation: false,
        RESOURCE_TYPES: {
            item: "items",
            "item-group": "item-groups",
            board: "boards",
            "board-group": "board-groups",
            workspace: "workspaces",
            "workspace-group": "workspace-groups",
        },
        // please don't change the order of the data
        ACCESS_LEVELS: {
            ITEM: "item",
            ITEM_GROUP: "item-group",
            BOARD: "board",
            BOARD_GROUP: "board-group",
            WORKSPACE: "workspace",
            WORKSPACE_GROUP: "workspace-group",
        },
        ROLES: {
            OWNER: "owner",
            ADMIN: "admin",
            MEMBER: "member",
            OBSERVER: "observer",
        },
        active_space_id: null,
        active_level_titles: {},
        current_participants: [],
        temp_roles_change_status: {},
        temp_chat_access_status: {},
        selected_ids_for_invite: new Set(),
        unselected_ids_for_remove: new Set(),
        space_only_removed_ids: new Set(),
        current_user_ids: [],
        has_fetched_members_with_role_from_quick_conversation: false,
        quick_conversation_cache_key: null,
        quick_conversation_selected_users: [],
        quick_conversation_assigned_user: null,
        quick_conversation_assigner_profile_id: null,
    }),
    getters: {
        active_conversation_owner_id: (state) => {
            if (!state.active_space_id) {
                return null;
            }

            return (
                useWorkspaceTree().getWorkspaceById(state.active_space_id)
                    ?.owner_id ?? null
            );
        },
        new_modified_participants: (state) => {
            return (
                state.user_profiles_with_roles
                    .filter((user_profile) => {
                        return (
                            user_profile.is_conversation_participant ||
                            user_profile.role === state.ROLES.OBSERVER
                        );
                    })
                    .map((user_profile) => user_profile?.id) ?? []
            );
        },
        is_anyones_roles_changed: (state) => {
            return Object.values(state.temp_roles_change_status ?? {}).includes(
                true,
            );
        },
        is_anyones_chat_access_changed: (state) => {
            return Object.values(state.temp_chat_access_status ?? {}).includes(
                true,
            );
        },
        user_can_edit: (state) => {
            const user_profile = state.user_profiles_with_roles.find(
                (user) => user?.id === useUserStore().profile?.id,
            );

            return canEditWorkPanelMembershipFromProfile(
                user_profile,
                state.ROLES,
            );
        },
    },
    actions: {
        closeInviteModel() {
            this.show_invite_model = false;
        },
        async getUserInvitations() {
            try {
                const { data } = await axios.get("/user/invitations");
                this.user_invitations = data?.data.lists;
            } catch (error) {
                console.log("error in console");
            }
        },
        resetInviteProfiles() {
            const all_profile_ids = this.selected_user_profile_ids.concat([
                ...new Set(this.selected_group_user_profile_ids),
            ]);
            this.user_profile_ids = [...new Set(all_profile_ids)];
        },
        restoreSelectedInviteProfiles() {
            this.selected_user_profile_ids = this.user_profile_ids;
        },
        clearSelectedInviteProfiles() {
            this.user_profile_ids = [];
            this.selected_user_profile_ids = [];
            this.selected_group_user_profile_ids = [];
        },
        setProfileIds(val) {
            this.user_profile_ids = val;
        },

        async getInvitedMembers(
            conversation,
            additionalResource = null,
            requestConfig = {},
        ) {
            const resource_type =
                additionalResource?.type ??
                this.RESOURCE_TYPES[conversation?.type];
            try {
                return await axios.get(
                    `/invites/chin/${resource_type}/${additionalResource?.type_id ?? conversation?.type_id}`,
                    requestConfig,
                );
            } catch (e) {
                if (isRequestCancelled(e)) {
                    console.log("request cancelled", e);
                    return;
                }
                if (
                    e.response?.status === HttpStatusCodes.UNPROCESSABLE_ENTITY
                ) {
                    Utilities().showErrorSnackbar(
                        "This conversation is no longer available.",
                    );
                }
                console.log("getting invitation profile ids");
            }
        },
        async invitedProfiles(
            optionalResource = null,
            requestConfig = {},
            { isStillValid = () => true } = {},
        ) {
            this.is_retrieving_invited_user_in_progress = true;

            //reset previous profile ids if any
            this.setProfileIds([]);

            let invited_profile_ids = [];
            const conversation = useConversation().active;

            if (
                Object.keys(useInvitation().RESOURCE_TYPES).includes(
                    conversation?.type,
                )
            ) {
                try {
                    const res = await this.getInvitedMembers(
                        conversation,
                        optionalResource,
                        requestConfig,
                    );
                    if (!res) {
                        this.is_retrieving_invited_user_in_progress = false;
                        return false;
                    }
                    const responseData = res?.data?.data;

                    const hasConversation = responseData?.has_conversation;
                    const participants = responseData?.participants?.length;

                    if (hasConversation) {
                        invited_profile_ids = responseData?.participants || [];
                    } else if (responseData?.invited_profile_ids?.length) {
                        invited_profile_ids = responseData?.invited_profile_ids;
                    } else if (responseData?.all_profile_ids) {
                        invited_profile_ids = responseData?.all_profile_ids;
                    }
                } catch (e) {
                    if (isRequestCancelled(e)) {
                        this.is_retrieving_invited_user_in_progress = false;
                        console.log("request cancelled", e);
                        return false;
                    }
                    if (
                        e.response?.status ===
                        HttpStatusCodes.UNPROCESSABLE_ENTITY
                    ) {
                        Utilities().showErrorSnackbar(
                            "This conversation is no longer available.",
                        );
                    }

                    Utilities().showErrorSnackbar(
                        "Failed to retrieve existing invitation",
                    );
                    console.log(e);
                    this.is_retrieving_invited_user_in_progress = false;
                    return false;
                }
            }

            if (conversation?.is_group || conversation?.is_group_one_off) {
                invited_profile_ids = conversation.getActiveParticipantIds();
            }

            if (!invited_profile_ids) {
                invited_profile_ids = [];
            }

            if (!isStillValid()) {
                this.is_retrieving_invited_user_in_progress = false;

                return false;
            }

            //invited_profile_ids.push(useWorkspace().getSpaceOwnerId())
            invited_profile_ids = Array.from(new Set(invited_profile_ids));

            this.setProfileIds(invited_profile_ids);

            if (conversation?.is_group || conversation?.is_group_one_off) {
                this.current_user_ids = [...invited_profile_ids];
            }

            this.restoreSelectedInviteProfiles();
            this.is_retrieving_invited_user_in_progress = false;
            return true;
        },
        async fetchMembersWithRole(
            resource_type,
            resource_id,
            requestConfig = {},
            { isStillValid = () => true } = {},
        ) {
            try {
                const res = await axios.get(
                    `invites/${resource_type}/${resource_id}`,
                    requestConfig,
                );

                if (!isStillValid()) {
                    return false;
                }

                this.temp_roles_change_status = {};
                this.temp_chat_access_status = {};

                useTeam().setAssigned(null);
                useTeam().setAssigner(null);
                useTeam().setOriginalAssigned(null);
                useTeam().setOriginalAssigner(null);

                useTeam().setAssigned(
                    res?.data?.data?.assigned_user_profile_id,
                );
                useTeam().setAssigner(
                    res?.data?.data?.assigner_user_profile_id,
                );
                useTeam().setOriginalAssigned(
                    res?.data?.data?.assigned_user_profile_id,
                );
                useTeam().setOriginalAssigner(
                    res?.data?.data?.assigner_user_profile_id,
                );
                this.has_conversation = res?.data?.data?.has_conversation;
                this.active_level_titles = res?.data?.data?.resource;
                this.user_profiles_with_roles =
                    res?.data?.data?.user_profiles?.map((user_profile) => {
                        const modified_user_profile = {
                            id: user_profile?.id,
                            role: user_profile?.role,
                            agent_id: user_profile?.agent_id,
                            email: user_profile?.email,
                            first_name: user_profile?.first_name,
                            full_name: user_profile?.full_name,
                            is_agent: user_profile?.is_agent,
                            is_organization_user:
                                user_profile?.is_organization_user,
                            non_registered:
                                user_profile?.is_organization_user === false,
                            access_level: "",
                            roles: user_profile?.roles ?? [],
                            is_conversation_participant: this.has_conversation
                                ? user_profile?.is_conversation_participant
                                : true,
                            has_parent_access: user_profile?.has_parent_access,
                            potential_assigners:
                                user_profile?.potential_assigners ?? [],
                        };

                        // Place members in their highest lineage section (SG → I).
                        const access_levels = Object.values(this.ACCESS_LEVELS);
                        let highest_lineage_level = "";
                        let highest_lineage_index = Number.NEGATIVE_INFINITY;

                        for (const role of user_profile?.roles ?? []) {
                            const access_level = role?.model_type;
                            const access_level_index = access_levels.findIndex(
                                (level) => level === access_level,
                            );

                            if (access_level_index > highest_lineage_index) {
                                highest_lineage_index = access_level_index;
                                highest_lineage_level = access_level;
                            }
                        }

                        modified_user_profile.access_level =
                            highest_lineage_level;

                        return modified_user_profile;
                    });

                // When conversation is not started, role profiles already
                // default to is_conversation_participant=true above. Align
                // current_participants with that default (API participant
                // flags are empty until the conversation exists).
                this.current_participants = this.has_conversation
                    ? (res?.data?.data?.user_profiles
                          ?.filter(
                              (user_profile) =>
                                  user_profile?.is_conversation_participant,
                          )
                          ?.map((user_profile) => user_profile?.id) ?? [])
                    : (this.user_profiles_with_roles
                          ?.filter(
                              (user_profile) =>
                                  user_profile?.is_conversation_participant,
                          )
                          ?.map((user_profile) => user_profile?.id) ?? []);
                this.current_user_ids =
                    res?.data?.data?.user_profiles.map(
                        (user_profile) => user_profile?.id,
                    ) ?? [];

                this.selected_ids_for_invite.clear();
                this.unselected_ids_for_remove.clear();
                this.space_only_removed_ids.clear();
                return true;
            } catch (error) {
                if (isRequestCancelled(error)) {
                    console.log("request cancelled", error);
                    return false;
                }
                if (
                    error.response?.status ===
                    HttpStatusCodes.UNPROCESSABLE_ENTITY
                ) {
                    Utilities().showErrorSnackbar(
                        "This conversation is no longer available.",
                    );
                } else {
                    Utilities().showErrorSnackbar(
                        "Failed to retrieve membership data",
                    );
                }
                console.log(error);
                return false;
            }
        },
        updateRole(role, user_id) {
            const normalizedId = normalizeMembershipProfileId(user_id);

            const findedUser = this.user_profiles_with_roles?.find(
                (user_profile) =>
                    normalizeMembershipProfileId(user_profile?.id) ===
                    normalizedId,
            );

            if (findedUser) {
                findedUser.role = role;
            }
        },
        updateParticipantAccess(user_id, is_participant) {
            if (user_id == null) {
                return;
            }

            const normalizedId = Number(user_id);
            const findedUser = this.user_profiles_with_roles?.find(
                (user_profile) => Number(user_profile?.id) === normalizedId,
            );
            if (findedUser) {
                findedUser.is_conversation_participant = is_participant;
            }
        },
        setPotentialAssignersForMember(user_id, potential_assigners = []) {
            const member = this.user_profiles_with_roles?.find(
                (user_profile) => user_profile?.id === user_id,
            );

            if (member) {
                member.potential_assigners = potential_assigners;
            }
        },
        ensureAssignmentParticipants(assigneeMember, assignerProfileId = null) {
            const teamStore = useTeam();

            if (
                !teamStore.assigned ||
                assigneeMember?.id == null ||
                Number(teamStore.assigned) !== Number(assigneeMember.id)
            ) {
                return;
            }

            const userStore = useUserStore();
            const accessLevel = useConversation().active?.type;

            const resolvedAssignerId =
                assignerProfileId ??
                getPreferredAssignerProfileId(
                    assigneeMember?.potential_assigners ?? [],
                ) ??
                userStore.getCurrentUserProfileId();

            const ensureProfileParticipant = (profile) => {
                if (!profile?.id) {
                    return;
                }

                this.addFakeMemberWithRoleIfNotExist(profile, accessLevel);
                this.updateParticipantAccess(profile.id, true);

                if (!profile.is_agent && profile.agent_id) {
                    this.updateParticipantAccess(profile.agent_id, true);
                }

                this.updateUserAddRemoveTrackStatus(profile.id, true);
                this.syncQuickConversationSelectedUser(profile.id, true);
            };

            if (assigneeMember) {
                ensureProfileParticipant(assigneeMember);
            }

            if (resolvedAssignerId == null) {
                return;
            }

            if (
                assigneeMember?.id != null &&
                Number(resolvedAssignerId) === Number(assigneeMember.id)
            ) {
                return;
            }

            const assignerProfile =
                userStore.getUserProfile(resolvedAssignerId) ?? {
                    id: resolvedAssignerId,
                };

            ensureProfileParticipant(assignerProfile);
        },
        addFakeMemberWithRoleIfNotExist(member, access_level) {
            const ownerProfile = ensureWorkPanelMemberRoleProfile({
                invitationStore: this,
                member,
                accessLevel: access_level,
            });

            if (!member.is_agent && member?.agent_id) {
                const agentId = normalizeMembershipProfileId(member.agent_id);
                const hasAgentProfile = (this.user_profiles_with_roles ?? []).some(
                    (user_profile) =>
                        normalizeMembershipProfileId(user_profile?.id) ===
                        agentId,
                );

                if (!hasAgentProfile) {
                    this.user_profiles_with_roles.push({
                        id: member.agent_id,
                        role: ownerProfile?.role ?? this.ROLES.MEMBER,
                        access_level,
                        is_conversation_participant:
                            ownerProfile?.is_conversation_participant ?? true,
                        has_parent_access: false,
                        is_agent: true,
                        owner_id: member.id,
                    });
                }
            }
        },
        removeMemberWithRole(member_id) {
            const normalizedMemberId = normalizeMembershipProfileId(member_id);
            const userProfiles = useUserStore().profiles;

            const member = userProfiles.find(
                (user_profile) =>
                    normalizeMembershipProfileId(user_profile?.id) ===
                    normalizedMemberId,
            );
            const removeIds = new Set(
                [member_id, member?.agent_id]
                    .filter((id) => id != null)
                    .map((id) => normalizeMembershipProfileId(id)),
            );

            this.user_profiles_with_roles =
                this.user_profiles_with_roles.filter((user_profile) => {
                    return !removeIds.has(
                        normalizeMembershipProfileId(user_profile?.id),
                    );
                });

            for (const id of [member_id, member?.agent_id]) {
                if (id == null) {
                    continue;
                }

                this.selected_ids_for_invite.delete(id);
                this.unselected_ids_for_remove.delete(id);
            }
        },
        isUserParticipant(user_id) {
            return Boolean(
                this.user_profiles_with_roles.find(
                    (user) => user.id === user_id,
                ),
            );
        },

        canEditRoleBasedOnLevel(current_conv_type, user_highest_access) {
            const access_levels = Object.values(this.ACCESS_LEVELS ?? {});
            const conv_order_index = access_levels.findIndex(
                (access_level) => current_conv_type === access_level,
            );
            const user_access_order_idnex = access_levels.findIndex(
                (access_level) => user_highest_access === access_level,
            );
            return conv_order_index >= user_access_order_idnex;
        },
        updateUserAddRemoveTrackStatus(user_id, checked) {
            if (checked) {
                if (!this.current_user_ids.includes(user_id)) {
                    this.selected_ids_for_invite.add(user_id);
                } else {
                    this.unselected_ids_for_remove.delete(user_id);
                }
                this.clearSpaceOnlyRemoval(user_id);
            } else {
                if (this.current_user_ids.includes(user_id)) {
                    this.unselected_ids_for_remove.add(user_id);
                } else {
                    this.selected_ids_for_invite.delete(user_id);
                }
            }
        },
        resolveSpaceOnlyRemovalProfileIds(userId) {
            if (userId == null) {
                return [];
            }

            const normalizedId = normalizeMembershipProfileId(userId);
            const profile =
                useUserStore().profiles.find(
                    (userProfile) =>
                        normalizeMembershipProfileId(userProfile?.id) ===
                        normalizedId,
                ) ??
                this.user_profiles_with_roles.find(
                    (userProfile) =>
                        normalizeMembershipProfileId(userProfile?.id) ===
                        normalizedId,
                );

            return [userId, profile?.agent_id]
                .filter((id) => id != null)
                .map((id) => normalizeMembershipProfileId(id));
        },
        markSpaceOnlyRemoval(user_id) {
            for (const id of this.resolveSpaceOnlyRemovalProfileIds(user_id)) {
                this.space_only_removed_ids.add(id);
            }
        },
        clearSpaceOnlyRemoval(user_id) {
            for (const id of this.resolveSpaceOnlyRemovalProfileIds(user_id)) {
                this.space_only_removed_ids.delete(id);
            }
        },
        clearSpaceOnlyRemovals() {
            this.space_only_removed_ids.clear();
        },
        canShowItemIcon(type) {
            return [
                this.ACCESS_LEVELS.ITEM,
                this.ACCESS_LEVELS.ITEM_GROUP,
            ].includes(type);
        },

        canShowBoardIcon(type) {
            return [this.ACCESS_LEVELS.BOARD].includes(type);
        },

        canShowSpaceIcon(type) {
            return [
                this.ACCESS_LEVELS.WORKSPACE,
                this.ACCESS_LEVELS.WORKSPACE_GROUP,
                this.ACCESS_LEVELS.BOARD_GROUP,
            ].includes(type);
        },

        async unInviteMembers(
            resource_type,
            resource_id,
            user_ids,
            resource_name,
            ignore_invitation_profile_ids = false,
            {
                remove_only_where_inactive = false,
                conversation_only_remove = false,
                conversation_id = null,
            } = {},
        ) {
            const workspace_store = useWorkspace();
            try {
                workspace_store.setInvitationInProgress(true);
                const payload = {
                    user_profile_ids: user_ids,
                    remove_only_where_inactive: Boolean(
                        remove_only_where_inactive,
                    ),
                    conversation_only_remove: Boolean(
                        conversation_only_remove,
                    ),
                };

                if (conversation_only_remove && conversation_id != null) {
                    payload.conversation_id = Number(conversation_id);
                }

                const { data } = await axios.post(
                    `un-invites/${resource_type}/${resource_id}`,
                    payload,
                );

                const optionalPayload = {
                    type: resource_type,
                    type_id: resource_id,
                };

                if (!ignore_invitation_profile_ids) {
                    await this.invitedProfiles(optionalPayload);
                }
                if (!resource_name) {
                    Utilities().showSuccessSnackbar(
                        data.message,
                        null,
                        "success",
                        5000,
                    );
                } else {
                    Utilities().showInfoSnackbar(
                        data.message,
                        null,
                        "info",
                        5000,
                    );
                }

                return true;
            } catch (error) {
                console.log(error);
                const api_message = error.response?.data?.message;
                if (typeof api_message === "string" && api_message.length > 0) {
                    Utilities().showErrorSnackbar(api_message);
                }
                return false;
            } finally {
                workspace_store.setInvitationInProgress(false);
            }
        },
        resetTempChangeTracking() {
            this.temp_roles_change_status = {};
            this.temp_chat_access_status = {};
        },

        addSelectedUserToQuickConversation(user_id) {
            this.quick_conversation_selected_users =
                syncQuickConversationSelectedUserIds(
                    this.quick_conversation_selected_users,
                    user_id,
                    true,
                );
        },

        /**
         * Seed Quick Conversation selection from Members-tab participation so
         * opening QC after membership edits restores instead of select-all.
         *
         * @param {string|null} [cacheKey]
         * @param {{ force?: boolean }} [options]
         */
        seedQuickConversationSelectionFromMembership(
            cacheKey = null,
            { force = false } = {},
        ) {
            if (
                this.has_fetched_members_with_role_from_quick_conversation &&
                !force
            ) {
                return;
            }

            this.quick_conversation_selected_users =
                resolveQuickConversationSelectedUsersFromParticipation(
                    this.user_profiles_with_roles,
                );
            this.quick_conversation_cache_key =
                resolveQuickConversationSeededCacheKey(
                    cacheKey,
                    resolveQuickConversationCacheKey(
                        this.RESOURCE_TYPES,
                        useConversation().active,
                    ),
                );
            this.has_fetched_members_with_role_from_quick_conversation = true;
        },

        syncQuickConversationSelectedUser(user_id, is_participant) {
            if (
                !this.has_fetched_members_with_role_from_quick_conversation ||
                user_id == null
            ) {
                return;
            }

            this.quick_conversation_selected_users =
                syncQuickConversationSelectedUserIds(
                    this.quick_conversation_selected_users,
                    user_id,
                    is_participant,
                );
        },

        resetQuickConversation() {
            this.has_fetched_members_with_role_from_quick_conversation = false;
            this.quick_conversation_cache_key = null;
            this.quick_conversation_selected_users = [];
            this.quick_conversation_assigned_user = null;
            this.quick_conversation_assigner_profile_id = null;
        },
        syncQuickConversationAssignmentFromTeamStore() {
            syncQuickConversationAssignmentFromTeamStore({
                invitationStore: this,
                teamStore: useTeam(),
                userStore: useUserStore(),
            });
        },
        reconcileQuickConversationAssignmentWithTeamStore() {
            return reconcileQuickConversationAssignmentWithTeamStore({
                invitationStore: this,
                teamStore: useTeam(),
                userStore: useUserStore(),
            });
        },
        setQuickConversationCheckedUsersAndAssigneeToUserProfilesWithRoles() {
            // Prefer selected IDs directly so newly staged members (not yet in
            // userStore lookup) are not cleared when syncing QC → Membership.
            const profileIdsSet = new Set(
                (this.quick_conversation_selected_users ?? []).map((id) =>
                    Number(id),
                ),
            );

            const userProfiles = useUserStore().getProfilesByIds(
                this.quick_conversation_selected_users,
            );
            userProfiles.forEach((userProfile) => {
                profileIdsSet.add(Number(userProfile.id));
                if (userProfile.agent_id) {
                    profileIdsSet.add(Number(userProfile.agent_id));
                }
            });

            for (const roleProfile of this.user_profiles_with_roles ?? []) {
                if (
                    roleProfile?.agent_id &&
                    profileIdsSet.has(Number(roleProfile.id))
                ) {
                    profileIdsSet.add(Number(roleProfile.agent_id));
                }
            }

            this.user_profiles_with_roles = this.user_profiles_with_roles.map(
                (user_profile) => {
                    return {
                        ...user_profile,
                        is_conversation_participant: profileIdsSet.has(
                            Number(user_profile.id),
                        ),
                    };
                },
            );
            this.reconcileQuickConversationAssignmentWithTeamStore();
        },
        async openMembershipPanelForQuickConversation() {
            const app_state_store = useAppState();
            const { chatPopover, toggleChatPopover } = Utilities();
            const wasChatPopoverOpen = chatPopover.value;
            const conversation = useConversation().active;
            const app_chat = useChat();

            if (
                conversation &&
                isMembershipTabActiveForConversation(app_chat, conversation) &&
                isMembershipDataPrefetchedForConversation(app_chat, conversation)
            ) {
                toggleChatPopover(true);

                return;
            }

            if (app_state_store.worklineFilterDrawer) {
                app_state_store.worklineFilterDrawer = false;
            }

            useConversation().toggleProductivityPanel(false);
            app_chat.setPreviousConvID(conversation?.id);
            app_chat.setPreviousTab(app_chat.tab);
            toggleChatPopover(true);

            const didOpenMembership = await openMembershipTabForConversation(
                app_chat,
                conversation,
            );

            if (!didOpenMembership) {
                if (!wasChatPopoverOpen) {
                    toggleChatPopover(false);
                }

                return;
            }
        },
    },
});
